import type { PieceId } from "./types";

export const PIECE_CUE_LENGTH = 500 as const;
export const HOLE_CUE_LENGTH = 100 as const;
export const GENERATOR_VERSION = "legacy-derived-generator-v3" as const;

// Keep the recovered piece stream stable while versioning the garbage-hole
// change independently. Both streams remain deterministic from the match seed
// so P2P peers and room players still produce identical game state.
const PIECE_CUE_ALGORITHM_VERSION = "legacy-derived-generator-v1" as const;
const HOLE_CUE_ALGORITHM_VERSION = "legacy-derived-generator-v3" as const;

export interface LegacyCues {
  seed: string;
  generatorVersion: typeof GENERATOR_VERSION;
  pieceCue: PieceId[];
  /** Zero-based columns in 0...9. */
  holeCue: number[];
}

export function normalizeSeed(seed: string | number | Uint8Array = 0): string {
  if (typeof seed === "string") return seed;
  if (typeof seed === "number") {
    if (!Number.isSafeInteger(seed)) throw new RangeError("Numeric seeds must be safe integers");
    return String(seed);
  }
  return Array.from(seed, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Stable 32-bit string mixer used only to seed the versioned PRNG. */
function seedState(text: string): number {
  let state = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    state ^= text.charCodeAt(index);
    state = Math.imul(state, 0x01000193);
  }
  state >>>= 0;
  return state === 0 ? 0x6d2b79f5 : state;
}

/** xorshift32, frozen as part of GENERATOR_VERSION. */
class LegacyPrng {
  private state: number;

  constructor(seed: string) {
    this.state = seedState(seed);
  }

  nextUint32(): number {
    let value = this.state;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.state = value >>> 0;
    return this.state;
  }

  nextUnit(): number {
    return this.nextUint32() / 0x1_0000_0000;
  }

  nextInt(exclusiveMaximum: number): number {
    return Math.floor(this.nextUnit() * exclusiveMaximum);
  }
}

function randomPiece(prng: LegacyPrng): PieceId {
  return (prng.nextInt(7) + 1) as PieceId;
}

/**
 * Versioned clean-room replacement for the lost server cue generator.
 *
 * It follows the recovered practice rule: draw 0...6 uniformly; when the
 * candidate repeats the previous piece, accept it 20% of the time and reroll
 * once 80% of the time. The accepted-repeat branch intentionally forgets the
 * previous value for one iteration, preserving the documented typo effect.
 */
export function generatePieceCue(seed: string | number | Uint8Array = 0): PieceId[] {
  const normalized = normalizeSeed(seed);
  const prng = new LegacyPrng(`${PIECE_CUE_ALGORITHM_VERSION}|pieces|${normalized}`);
  const cue: PieceId[] = [];
  let previous: PieceId | undefined;

  for (let index = 0; index < PIECE_CUE_LENGTH; index += 1) {
    let candidate = randomPiece(prng);
    let forgetPrevious = false;

    if (candidate === previous) {
      if (prng.nextUnit() < 0.8) {
        candidate = randomPiece(prng);
      } else {
        forgetPrevious = true;
      }
    }

    cue.push(candidate);
    previous = forgetPrevious ? undefined : candidate;
  }

  return cue;
}

/** One independent, deterministic, seed-randomized hole column for each garbage row. */
export function generateHoleCue(seed: string | number | Uint8Array = 0): number[] {
  const normalized = normalizeSeed(seed);
  const prng = new LegacyPrng(`${HOLE_CUE_ALGORITHM_VERSION}|holes|${normalized}`);
  const cue: number[] = [];

  for (let row = 0; row < HOLE_CUE_LENGTH; row += 1) {
    cue.push(prng.nextInt(10));
  }

  return cue;
}

export function generateLegacyCues(seed: string | number | Uint8Array = 0): LegacyCues {
  const normalized = normalizeSeed(seed);
  return {
    seed: normalized,
    generatorVersion: GENERATOR_VERSION,
    pieceCue: generatePieceCue(normalized),
    holeCue: generateHoleCue(normalized),
  };
}

export function assertPieceCue(cue: readonly number[]): asserts cue is readonly PieceId[] {
  if (cue.length !== PIECE_CUE_LENGTH || !cue.every((piece) => Number.isInteger(piece) && piece >= 1 && piece <= 7)) {
    throw new RangeError(`pieceCue must contain exactly ${PIECE_CUE_LENGTH} piece IDs in 1...7`);
  }
}

export function assertHoleCue(cue: readonly number[]): void {
  if (cue.length !== HOLE_CUE_LENGTH || !cue.every((hole) => Number.isInteger(hole) && hole >= 0 && hole < 10)) {
    throw new RangeError(`holeCue must contain exactly ${HOLE_CUE_LENGTH} columns in 0...9`);
  }
}
